import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { connect } from 'react-redux';
import { Drawer, Spin, Steps, Space, Button, Modal, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import * as BillApi from '@/api/bill';
import * as SettleApi from '@/api/bill/settle';
import * as helper from '@/pages/reservation/checkOut/helper';
import { BizErrorCode } from '@/api/bizCode';

import { setSettlementAction } from '@/redux/actions/bill';

import {
  initOrderState,
  setOrderState,
  setSettleStep,
  initOrderInfo,
  resetOrderInfo,
  initCheckRoomList,
  initSettleInfo,
} from '@/redux/actions/bill/settle';

import PayModal from '@/components/Payment/payModal';
import { PayResult, PayModalRef, PayWay } from '@/components/Payment/interface';
import usePortalConfirm from '@/components/PortalConfirm';
import {
  showCheckOutPrompt,
  showRoomCardConfirm,
  showWardRoundConfirm,
} from '@/components/Reservation/Modal';
import checkInService from '@/api/checkIn';

import Checkout from './checkOut';
import CheckRoom from './checkRoom';
import Settlement from './settlement';
import styles from './index.module.scss';

import { OpenSettleDrawerProps, SettleDrawerProps } from '../interface';
import validator from './validator';
import BillRefund from '../refund';

const { Step } = Steps;

const Settle: React.FC<SettleDrawerProps> = forwardRef((props, ref) => {
  const {
    orderStateLoading,
    orderState,
    step,
    orderInfo,
    checkRoomList,
    settleInfo,
    settlementAction,
  } = props;

  const payModalRef = useRef<PayModalRef>();

  const defaultRefundAmounts = {
    roomRate: 0, // 房费
    deposit: 0, // 押金
  };

  // Payment & PayModal props
  const [initPayTypes, setInitPayTypes] = useState(false);
  const [payType, setPayType] = useState(3);
  const [certNo, setCertNo] = useState('');
  const [wyuid, setWyuid] = useState('');

  // self states
  const [checkOutDisabled, setCheckOutDisabled] = useState(true);
  const [nextStepDisabled, setNextStepDisabled] = useState(false);
  const [checkOutLoading, setCheckOutLoading] = useState(false);
  const [useDeposit, setUseDeposit] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [refundAmounts, setRefundAmounts] = useState(defaultRefundAmounts);
  const [payedAmount, setPayedAmount] = useState(0);

  const [resvNo, setResvNo] = useState(''); // 主订单号
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [settlementNo, setSettlementNo] = useState(''); // 结账单号
  const [refundDrawerVisible, setRefundDrawerVisible] = useState(false);
  const confirmPortal = usePortalConfirm();

  // 冲调原因
  const [reasons, setReasons] = useState<any[]>([]);

  const steps = [
    {
      title: intl.get('app_enum_room_status_checkout'),
      content: <Checkout resvNo={resvNo} adjustReasons={reasons} />,
    },
    {
      title: intl.get('bill_check_room'),
      content: <CheckRoom resvNo={resvNo} />,
    },
    {
      title: intl.get('app_checkout'),
      content: (
        <Settlement
          resvNo={resvNo}
          payedAmount={payedAmount}
          useDeposit={(val) => setUseDeposit(val)}
          initPayTypes={initPayTypes}
          selectPayType={(type) => setPayType(type)}
          setCertNo={(no) => setCertNo(no)}
          setWyuid={(uid) => setWyuid(uid)}
        />
      ),
    },
  ];

  const openDrawer = (params: OpenSettleDrawerProps) => {
    setResvNo(params.resvNo);
    helper.getReverseReasons().then((list) => {
      setReasons(list);
    });
    setDrawerVisible(true);
  };

  useImperativeHandle(ref, () => ({
    openDrawer,
  }));

  const payResultCallback = (payResult: PayResult) => {
    if (payResult.payed && payResult.result) {
      if (useDeposit) {
        message.success(intl.get('app_checkout_success'));
        setDrawerVisible(false);
        // reset order state
        props.setOrderState(0);
        // reset settle step
        props.setSettleStep(0);

        // callback parent component
        props.settleCallback();
      } else {
        // 先收款后退款 hack
        setPayedAmount(Math.abs(settleInfo.waitPayment));
      }
    }

    if (payResult.payed && !payResult.result) {
      message.error(intl.get('app_checkout_fail'));
    }
  };

  const resetPayModalProps = () => {
    setInitPayTypes(false);
    setCertNo('');
    setWyuid('');
  };

  const checkSettleStep = async () => {
    try {
      const res = await SettleApi.fetchSettleStepInfo({ resvNo });
      if (res && res.status) {
        const { checkRoom } = res.data;
        // 已查房，跳转第三步
        if (!checkRoom) {
          props.initSettleInfo({
            resvNo,
            useDeposit: true, // 押金抵房费
          });
          props.setSettleStep(2);
          setNextStepDisabled(false);
          setInitPayTypes(true);
        } else {
          // 未查房
          setNextStepDisabled(true);
          props.setSettleStep(1);
          props.initCheckRoomList({
            resvNo,
          });
        }
      }
    } catch (err) {
      setNextStepDisabled(false);
    }
  };

  // dynamic step by order state & rounds state
  const settleEntry = async (state) => {
    // 预订单、入住单
    if (state === 1 || state === 2) {
      props.initOrderInfo({
        resvNo,
      });
      props.setSettleStep(0);
    } else if (state === 3 || state === 4) {
      // 预订取消单、退房单
      checkSettleStep();
    }
  };

  const changeVisible = (visible) => {
    if (visible) {
      props.initOrderState({
        resvNo,
      });
    } else {
      // reset states and store after drawer closed
      setCheckOutDisabled(true);
      setNextStepDisabled(false);
      props.resetOrderInfo();
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setPayedAmount(0);
    setUseDeposit(true);
    setSettlementNo('');
    setRefundDrawerVisible(false);
    resetPayModalProps();

    // reset order state & settle step
    props.setOrderState(0);
    props.setSettleStep(0);

    // callback parent component
    props.settleCallback();
  };

  // 生成结账单
  const generateSettleOrder = () => {
    let amount = 0;
    // 收款
    if (settleInfo.waitPayment < 0) {
      amount = settleInfo.waitPayment;
    }
    // 退款
    if (settleInfo.waitPayment === 0 && settleInfo.settleAmount > 0) {
      amount = settleInfo.settleAmount;
    }
    const params = {
      amount, // 金额
      resvNo, // 订单号
      deposit: settleInfo.refundDeposit, // 应退押金
      // paymentType: payWay, // 支付方式
      useDeposit, // 是否抵用押金
      trigger: '结账第三步执行结账时',
      operationPage: '结账抽屉，结账',
    };
    return BillApi.generateSettleOrder(params);
  };

  // 收支平衡，直接结账
  const submit = async () => {
    try {
      setSubmitLoading(true);
      const res = await generateSettleOrder();

      if (res && res.status) {
        // 确认结账
        const result = await BillApi.confirmSettle(res.data?.no);
        if (result && result.status) {
          message.success(intl.get('app_checkout_success'));
          closeDrawer();
        } else {
          message.error(result.msg || intl.get('app_checkout_fail'));
        }
      } else {
        message.error(res.msg || intl.get('bill_part_refund_generate_fail'));
      }

      setSubmitLoading(false);
    } catch (err) {
      setSubmitLoading(false);
    }
  };

  // 收款，唤起收银台
  const gotoPay = async (confirm?: boolean) => {
    // 付款单需confirm后下单
    if (!confirm && payType === PayWay.Fukuandan) {
      payModalRef.current.openModal({
        payType,
        showFukuanDanConfirm: true,
      });
      return;
    }

    try {
      setPayLoading(true);
      const res = await generateSettleOrder();

      if (res && res.status) {
        // store settlementNo & settlementAction
        setSettlementNo(res.data?.no);
        props.setSettlementAction('pay');

        // open payModal
        payModalRef.current.openModal({
          payAmount: +settleInfo.waitPayment,
          payType,
          payOrderType: 102,
          paymentNo: res.data?.paymentNo,
          certNo,
          wyuid,
          extraParams: {
            trigger: '结账第三步执行结账时',
            operationPage: '结账抽屉，结账',
          },
        });
      } else {
        message.error(res.msg || intl.get('bill_part_refund_generate_fail'));
      }

      setPayLoading(false);
    } catch (err) {
      setPayLoading(false);
    }
  };

  // 退款，打开退款抽屉
  const gotoRefund = async () => {
    // 先收房费后退押金 hack
    if (payedAmount > 0 && settlementNo) {
      // set refund amounts
      setRefundAmounts({
        roomRate: 0,
        deposit: settleInfo.refundDeposit,
      });

      // store settlementAction
      props.setSettlementAction('refund');
      setRefundDrawerVisible(true);

      setDrawerVisible(false);

      return;
    }

    try {
      setRefundLoading(true);
      const res = await generateSettleOrder();

      if (res && res.status) {
        // set refund amounts
        setRefundAmounts({
          roomRate: res.data?.amount,
          deposit: res.data?.deposit,
        });

        // store settlementNo & settlementAction
        setSettlementNo(res.data?.no);
        setRefundDrawerVisible(true);

        props.setSettlementAction('refund');
        setDrawerVisible(false);
      } else {
        message.error(res.msg || intl.get('bill_part_refund_generate_fail'));
      }

      setRefundLoading(false);
    } catch (err) {
      setRefundLoading(false);
    }
  };

  // 校验是否未查房
  const handleCheckErr: (response) => void = async (response) => {
    await showWardRoundConfirm(confirmPortal, response, 'checkOut');
  };

  // 校验房卡是否已退
  const handleCardErr: (response) => void = async (response) => {
    await showRoomCardConfirm(confirmPortal, response);
  };

  // 退房二次确认
  const showCheckOutConfirm = () => {
    return showCheckOutPrompt(confirmPortal).then((shouldContinue) => {
      if (shouldContinue) return;
      throw new Error('Abort');
    });
  };

  const getOverTimePriceRemark = (recNo: string, overtimePriceAdjustList: any[]) => {
    const item = overtimePriceAdjustList?.find((obj) => obj.recNo === recNo);
    if (item) {
      const reasonText = reasons?.find((re) => re.value === item.adjustReason)?.label;
      return `${reasonText}${item.adjustRemarks ? '，' : ''}${item.adjustRemarks || ''}`;
    }
    return null;
  };

  // 提交退房
  const confirmCheckout = async () => {
    try {
      setCheckOutLoading(true);

      const { cancelReservationList, checkOutRoomList, overtimePriceAdjustList } = orderInfo;

      // 输入校验拦截
      const isReservationAmountsValid = cancelReservationList.some((item) => !!item?.amountInvalid);
      const isCheckOutAmountsValid = checkOutRoomList.some((item) => !!item?.amountInvalid);

      if (isReservationAmountsValid || isCheckOutAmountsValid) {
        message.warning(intl.get('bill_credit_check_input'));
        setCheckOutLoading(false);
        return;
      }

      const { isValid, msg } = validator.checkAdjustListValid(overtimePriceAdjustList);
      if (!isValid) {
        message.warning(msg);
        setCheckOutLoading(false);
        return;
      }

      const params = {
        resvNo,
        cancelReservationItems: cancelReservationList.map((item) => {
          return {
            amount: item.liquidatedDamages,
            recNo: item.recNo,
          };
        }),
        checkOutItems: checkOutRoomList.map((item) => {
          const overTimePriceRemark = getOverTimePriceRemark(item.recNo, overtimePriceAdjustList);
          return {
            overTime: item.overTime,
            overTimePrice: item.overtimePrice,
            recNo: item.recNo,
            roomId: item.roomId,
            roomNo: item.roomNo,
            overTimePriceReceivable: item.overtimePriceReceivable,
            overTimePriceRemark,
          };
        }),
      };

      if (params.checkOutItems.length) {
        // 有退房的房间 校验房卡、是否查房
        const validateErr = await checkInService
          .checkOutValidate({ resvNo, rooms: params.checkOutItems })
          .then(
            () => null,
            (errRes) => errRes,
          );
        if (validateErr) {
          await handleCheckErr(validateErr);
          await handleCardErr(validateErr);
          throw validateErr;
        }
        await showCheckOutConfirm();
      }
      const res = await SettleApi.settleResvOrder(params);
      if (res && res.status) {
        message.success(intl.get('drawer_checkout_message_checkout_success'));

        checkSettleStep();
      } else {
        message.success(res.msg || intl.get('drawer_checkout_message_checkout_fail'));
      }

      setCheckOutLoading(false);
    } catch (err) {
      setCheckOutLoading(false);
    }
  };

  const nextStep = () => {
    props.initSettleInfo({
      resvNo,
      useDeposit: true, // 押金抵房费
    });
    props.setSettleStep(2);
    setInitPayTypes(true);
  };

  // watch order state change
  useEffect(() => {
    if (!drawerVisible) return;
    // not error state
    if (orderState && orderState !== 0) {
      settleEntry(orderState);
    }
  }, [orderState, drawerVisible]);

  // watch checkRoomList, dynamic nextStep button disabled status
  useEffect(() => {
    if (!drawerVisible) return;
    if (checkRoomList.length === 0) {
      setNextStepDisabled(false);
    } else {
      const hasNotCheckedRoom = checkRoomList.some((item) => item.status === 1);
      setNextStepDisabled(!!hasNotCheckedRoom);
    }
  }, [checkRoomList, drawerVisible]);

  useEffect(() => {
    if (!drawerVisible) return;
    if (
      orderInfo &&
      (orderInfo.cancelReservationList.length !== 0 || orderInfo.checkOutRoomList.length !== 0)
    ) {
      setCheckOutDisabled(false);
    }
  }, [orderInfo, drawerVisible]);

  const dynamicSettleBtn = () => {
    // 先收房费后退押金 hack
    if (payedAmount && !useDeposit) {
      return (
        <Button type="primary" danger loading={refundLoading} onClick={() => gotoRefund()}>
          {intl.get('bill_config_refound')}
        </Button>
      );
    }

    // 直接结账
    // 待支付金额为0，且应退押金为0，且应退房费小于等于0
    if (
      settleInfo.waitPayment === 0 &&
      settleInfo.refundDeposit === 0 &&
      settleInfo.settleAmount <= 0
    ) {
      return (
        <Button type="primary" loading={submitLoading} onClick={() => submit()}>
          {intl.get('bill_part_refund_confirm_settle')}
        </Button>
      );
    }

    // 退款
    // 待支付金额为0，且应退押金大于0或应退房费大于0
    if (
      settleInfo.waitPayment === 0 &&
      (settleInfo.refundDeposit > 0 || settleInfo.settleAmount > 0)
    ) {
      return (
        <Button type="primary" danger loading={refundLoading} onClick={() => gotoRefund()}>
          {intl.get('bill_config_refound')}
        </Button>
      );
    }

    if (settleInfo.waitPayment < 0) {
      return (
        <Button type="primary" loading={payLoading} onClick={() => gotoPay()}>
          {intl.get('bill_credit_collection')}
        </Button>
      );
    }

    return null;
  };

  const stepButton = () => {
    switch (step) {
      case 0:
        return (
          <Button
            type="primary"
            disabled={checkOutDisabled}
            loading={checkOutLoading}
            onClick={() => confirmCheckout()}
          >
            {intl.get('app_enum_room_status_checkout')}
          </Button>
        );
      case 1:
        return (
          <Button type="primary" disabled={nextStepDisabled} onClick={() => nextStep()}>
            {intl.get('app_next')}
          </Button>
        );
      case 2:
        return dynamicSettleBtn();
      default:
        return null;
    }
  };

  const drawerFooter = () => {
    return (
      <Space size={16} style={{ marginLeft: 8 }}>
        {stepButton()}
        <Button onClick={() => closeDrawer()}>{intl.get('app_confirm_cancel_btn')}</Button>
      </Space>
    );
  };

  return (
    <Drawer
      width="90%"
      open={drawerVisible}
      title={intl.get('app_checkout')}
      maskClosable={false}
      closable={false}
      afterOpenChange={(visible) => changeVisible(visible)}
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={drawerFooter()}
    >
      <PayModal
        ref={payModalRef}
        payResultCallback={(payResult) => payResultCallback(payResult)}
        onConfirmOk={() => gotoPay(true)}
      />
      <BillRefund
        refundDrawerVisible={refundDrawerVisible}
        refundAmounts={refundAmounts}
        settlementNo={settlementNo}
        settlementAction={settlementAction}
        refundCallback={(status) => {
          setRefundDrawerVisible(false);
          setDrawerVisible(status);
          // parent component callback
          if (!status) props.settleCallback();
        }}
      />

      <Steps labelPlacement="vertical" className={styles['settle-steps']} current={step}>
        {steps.map((item) => (
          <Step key={item.title} title={item.title} />
        ))}
      </Steps>
      <Spin spinning={orderStateLoading}>
        <section className={styles['step-content']}>{steps[step].content}</section>
      </Spin>
    </Drawer>
  );
});

const mapStateToProps = (state) => {
  const { settlementAction } = state.bill;
  const {
    orderStateLoading,
    orderState,
    step,
    orderInfo,
    checkRoomList,
    settleInfo,
  } = state.settle;

  return {
    orderStateLoading,
    orderState,
    step,
    orderInfo,
    checkRoomList,
    settleInfo,
    settlementAction,
  };
};

export default connect(
  mapStateToProps,
  {
    setSettlementAction,
    initOrderState,
    setOrderState,
    setSettleStep,
    initOrderInfo,
    resetOrderInfo,
    initCheckRoomList,
    initSettleInfo,
  },
  null,
  { forwardRef: true },
)(Settle);
